import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
    applyColgroupWidths,
    convertFileToBase64,
    FIRST_ELEMENT_INDEX,
    Indent,
    LineHeight,
    NumberUtils,
    OrderedTextAlign,
    PageBreak,
    PlaceholderMention,
    RichImage,
    TableCellStyle,
    TableStyle,
    TabSpan,
} from '@modeso/aumico-lib-core-fe';
import { SUPPORTED_PLACE_HOLDERS } from '@modeso/aumico-lib-core-fe/constants/constants';
import { IAopProposal, IAppropriationOfProfitEntry } from '@modeso/types__aumico-lib-annualreport-be';
import type { AnyExtension } from '@tiptap/core';
import { Editor } from '@tiptap/core';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { localizationKeys } from '../../constants/localizationKeys';

const debug = Debug('aumico:aumico-lib-annualreport-fe:ProposalComponent');

const AVAILABLE_RETAINED_EARNINGS_ROW_NAME = 'Available Retained Earnings';
const CARRIED_FORWARD_ROW_NAME = 'Carried Forward';
@UntilDestroy()
@Component({
    selector: 'aumico-annualReport-proposal-component',
    templateUrl: 'proposal.component.html',
    styleUrls: ['proposal.component.scss'],
})
export class ProposalComponent implements OnInit, OnChanges, OnDestroy {
    @Output() proposalOutput = new EventEmitter<IAopProposal>();
    @Output() proposalPrefilledValue = new EventEmitter<IAopProposal>();
    @Output() aopDescriptionChange = new EventEmitter<string>();

    @Input() currentYearDate: string | undefined;

    @Input() previousYearDate: string | undefined;

    @Input() currency: string;

    // refer to finNumber pipe for reference on values
    @Input() numberFormat = '1.2-2';

    // we have it as input incase of edit mode
    @Input() savedProposal: IAopProposal | undefined;

    @Input() previousProposal: IAopProposal | undefined;

    @Input() removeEmptyLines: boolean;

    @Input() currentAvailableRetainedEarnings: number | undefined;

    @Input() previousAvailableRetainedEarnings: number | undefined;

    @Input() reportLocale: string;

    @Input() isSavedBefore = false;

    @Input() savedAopDescription: string | undefined;
    @Input() previousAopDescription: string | undefined;

    // ─── AOP Description tiptap editor ───────────────────────────────────────
    @ViewChild('aopDescriptionHost') aopDescriptionHost?: ElementRef<HTMLDivElement>;
    tiptapEditor: Editor | null = null;
    aopDescriptionCheckboxControl = new UntypedFormControl(false);
    showAopDescription = false;
    aopDescription = '';
    showRemoveAopDescriptionConfirm = false;

    currentCarriedForwardValue: number | undefined;
    previousCarriedForwardValue: number | undefined;

    form: UntypedFormGroup | undefined;
    dataSource: UntypedFormArray | undefined;
    readonly displayedColumns = ['name', 'currentValue', 'previousValue', 'remove'];
    descriptionEditMode: boolean = false;
    private tableEntries: IAppropriationOfProfitEntry[] = [];
    private local = localizationKeys;

    proposalDescription = '';

    private formChange: Subscription | undefined;
    proposalCarriedForwardText: string;
    proposalAvailableRetainedEarningText: string;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private localizedTexts: I18NextService,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {
        this.addSvgIcon('aumico-pattern-icon-remove');
        this.addSvgIcon('aumico-pattern-icon-edit-write');
    }

    ngOnDestroy(): void {
        this.tiptapEditor?.destroy();
        this.tiptapEditor = null;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['savedAopDescription'] && changes['savedAopDescription'].currentValue != null) {
            this.initAopDescription(changes['savedAopDescription'].currentValue as string);
        }
        if (!this.aopDescription && changes['previousAopDescription'] && changes['previousAopDescription'].currentValue != null) {
            this.initAopDescription(changes['previousAopDescription'].currentValue as string);
        }

        if (changes['savedProposal'] != null && changes['savedProposal'].currentValue != null && !this.isSavedBefore) {
            this.handleProposalSavedData();
        }
        if (
            changes['previousProposal'] != null &&
            changes['previousProposal'].currentValue != null &&
            !this.isSavedBefore
        ) {
            this.handleProposalAutofillData();
        }
        if (
            changes['currentAvailableRetainedEarnings'] != null &&
            changes['currentAvailableRetainedEarnings'].currentValue != null
        ) {
            this.calculateCurrentCarriedForwardValue();
            this.updateCalculatedTableData();
            // Update the carried forward label if not in edit mode
            if (!this.savedProposal && !this.previousProposal) {
                this.proposalCarriedForwardText = this.getProposalCarriedForwardTextLabel();
                this.proposalAvailableRetainedEarningText = this.getProposalAvailableRetainedEarningTextLabel();
                this.proposalDescription = this.getProposalDescriptionLabel();
                this.proposalPrefilledValue.emit(this.prepareProposalOutputData());
            }
        }
        if (
            changes['previousAvailableRetainedEarnings'] != null &&
            changes['previousAvailableRetainedEarnings'].currentValue != null
        ) {
            this.calculatePreviousCarriedForwardValue();
            this.updateCalculatedTableData();
            // Update the carried forward label if not in edit mode
            if (!this.savedProposal && !this.previousProposal) {
                this.proposalCarriedForwardText = this.getProposalCarriedForwardTextLabel();
                this.proposalAvailableRetainedEarningText = this.getProposalAvailableRetainedEarningTextLabel();
                this.proposalDescription = this.getProposalDescriptionLabel();
                this.proposalPrefilledValue.emit(this.prepareProposalOutputData());

            }
        }
    }

    ngOnInit(): void {
        debug('ngOnInit');

        this.initializeForm();
        this.applyLocalizations();
    }

    private applyLocalizations(): void {
        if (
            (!this.savedProposal?.proposalDescription?.length && !this.previousProposal?.proposalDescription?.length) ||
            (!this.savedProposal?.proposalCarriedForwardText?.length &&
                !this.previousProposal?.proposalCarriedForwardText?.length) ||
            (!this.savedProposal?.proposalAvailableRetainedEarningText?.length &&
                !this.previousProposal?.proposalAvailableRetainedEarningText?.length) ||
            (!this.savedProposal && !this.previousProposal)
        ) {
            this.setDefaultProposalDescriptionValue();
        }
    }

    private handleProposalSavedData(): void {
        //remove the first empty entry that was added in the initialization

        if (this.tableEntries[FIRST_ELEMENT_INDEX]?.name === null) {
            this.tableEntries.splice(FIRST_ELEMENT_INDEX, 1);
        } else {
            this.tableEntries.splice(FIRST_ELEMENT_INDEX, this.tableEntries.length - 2);
        }

        // add the saved entries on top of the last 2 entries (carried forward & available retained earnings)
        const startIndexForNewEntries = this.tableEntries.length - 2;
        this.tableEntries.splice(startIndexForNewEntries, FIRST_ELEMENT_INDEX, ...this.savedProposal.proposalEntries);
        this.proposalDescription = this.getProposalDescriptionLabel();
        this.calculateCurrentCarriedForwardValue();
        this.calculatePreviousCarriedForwardValue();
        this.updateCalculatedTableData();

        this.proposalAvailableRetainedEarningText = this.getProposalAvailableRetainedEarningTextLabel();
        this.proposalCarriedForwardText = this.getProposalCarriedForwardTextLabel();

        const preparedOutput: IAopProposal = this.prepareProposalOutputData();
        this.proposalPrefilledValue.emit(preparedOutput);
    }

    private handleProposalAutofillData(): void {
        //remove the first empty entry that was added in the initialization
        if (this.tableEntries[FIRST_ELEMENT_INDEX]?.name === null) {
            this.tableEntries.splice(FIRST_ELEMENT_INDEX, 1);
        } else {
            this.tableEntries.splice(FIRST_ELEMENT_INDEX, this.tableEntries.length - 2);
        }
        // copy previous year values to current year
        const currentTableEntriesFromPreviousTableEntries: IAppropriationOfProfitEntry[] =
            this.previousProposal.proposalEntries.map((entry) => {
                return {
                    name: entry.name,
                    currentValue: null,
                    previousValue: entry.currentValue,
                };
            });
        // add the autofilled entries on top of the last 2 entries (carried forward & available retained earnings)
        const startIndexForNewEntries = this.tableEntries.length - 2;
        this.tableEntries.splice(
            startIndexForNewEntries,
            FIRST_ELEMENT_INDEX,
            ...currentTableEntriesFromPreviousTableEntries,
        );

        this.calculateCurrentCarriedForwardValue();
        this.calculatePreviousCarriedForwardValue();
        this.updateCalculatedTableData();
        this.proposalDescription = this.getAutofillProposalDescriptionLabel();
        this.proposalAvailableRetainedEarningText = this.getAutofillProposalAvailableRetainedEarningTextLabel();
        this.proposalCarriedForwardText = this.getAutofillProposalCarriedForwardTextLabel();

        this.changeDetectorRef.detectChanges();

        const preparedOutput: IAopProposal = this.prepareProposalOutputData();
        this.proposalPrefilledValue.emit(preparedOutput);
    }

    private getProposalDescriptionLabel(): string {
        if (this.savedProposal?.proposalDescription) {
            return this.savedProposal.proposalDescription;
        }
        return this.getDynamicProposalDescription();
    }
    private getProposalAvailableRetainedEarningTextLabel(): string {
        if (this.savedProposal?.proposalAvailableRetainedEarningText) {
            return this.savedProposal.proposalAvailableRetainedEarningText;
        }
        return this.getDynamicAvailableRetainedEarningText();
    }
    private getProposalCarriedForwardTextLabel(): string {
        if (this.savedProposal?.proposalCarriedForwardText) {
            return this.savedProposal.proposalCarriedForwardText;
        }
        return this.getDynamicCarriedForwardLabel();
    }
    private getAutofillProposalDescriptionLabel(): string {
        if (this.previousProposal?.proposalDescription) {
            return this.previousProposal.proposalDescription;
        }
        return this.getDynamicProposalDescription();
    }
    private getAutofillProposalAvailableRetainedEarningTextLabel(): string {
        if (this.previousProposal?.proposalAvailableRetainedEarningText) {
            return this.previousProposal.proposalAvailableRetainedEarningText;
        }
        return this.getDynamicAvailableRetainedEarningText();
    }
    private getAutofillProposalCarriedForwardTextLabel(): string {
        if (this.previousProposal?.proposalCarriedForwardText) {
            return this.previousProposal.proposalCarriedForwardText;
        }
        return this.getDynamicCarriedForwardLabel();
    }

    private setDefaultProposalDescriptionValue(): void {
        this.proposalDescription = this.getProposalDescriptionLabel();
        this.proposalAvailableRetainedEarningText = this.getProposalAvailableRetainedEarningTextLabel();
        // Use dynamic label instead of static one
        this.proposalCarriedForwardText = this.getProposalCarriedForwardTextLabel();
        const preparedOutput: IAopProposal = this.prepareProposalOutputData();
        this.proposalPrefilledValue.emit(preparedOutput);
        this.proposalOutput.emit(preparedOutput);
    }

    proposalDescriptionEdited(): void {
        this.descriptionEditMode = !this.descriptionEditMode;
        const preparedOutput: IAopProposal = this.prepareProposalOutputData();
        this.proposalOutput.emit(preparedOutput);
    }

    private addFormGroup(dataSource: any): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(dataSource).forEach((key) => {
            dataSourceFormGroup[key] = new UntypedFormControl(dataSource[key]);
        });

        return new UntypedFormGroup(dataSourceFormGroup);
    }

    private getAllAsFormArray(): UntypedFormArray {
        const formGroups = this.tableEntries.map((entry) => this.addFormGroup(entry));
        return new UntypedFormArray(formGroups);
    }

    private initializeForm(): void {
        if (!this.savedProposal && !this.previousProposal) {
            this.tableEntries.push(
                // adding an empty row in edit mode
                {
                    name: null,
                    currentValue: null,
                    previousValue: null,
                },
            );
        }

        this.tableEntries.push(
            {
                name: CARRIED_FORWARD_ROW_NAME,
                currentValue: null,
                previousValue: null,
            },
            {
                name: AVAILABLE_RETAINED_EARNINGS_ROW_NAME,
                currentValue: null,
                previousValue: null,
            },
        );
        this.form = this.formBuilder.group({
            dataSource: this.formBuilder.array([]),
        });
        this.dataSource = this.getAllAsFormArray();
        this.form.setControl('dataSource', this.dataSource);

        this.updateCalculatedTableData();
        const preparedOutput: IAopProposal = this.prepareProposalOutputData();
        this.proposalOutput.emit(preparedOutput);
        this.proposalPrefilledValue.emit(preparedOutput);
        // listen to form value changes
        this.onFormValueChange();
    }

    private updateForm(): void {
        this.dataSource = this.getAllAsFormArray();
        this.form?.setControl('dataSource', this.dataSource);
    }

    private onFormValueChange(): void {
        if (this.formChange != null) {
            this.formChange.unsubscribe();
        }
        this.formChange = this.form.valueChanges
            .pipe(
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
                untilDestroyed(this),
            )
            .subscribe((value) => {
                this.tableEntries = value.dataSource;
                this.calculateCurrentCarriedForwardValue();
                this.calculatePreviousCarriedForwardValue();
                const preparedOutput: IAopProposal = this.prepareProposalOutputData();
                debug('proposalOutput', preparedOutput);
                this.proposalOutput.emit(preparedOutput);

            });
    }

    private calculateCurrentCarriedForwardValue(): void {
        const carriedValues: Array<number | null> = [];
        // we need to omit the last 2 entries from calculation
        for (let rowIndex = FIRST_ELEMENT_INDEX; rowIndex < this.tableEntries.length - 2; rowIndex++) {
            carriedValues.push(this.tableEntries[rowIndex].currentValue);
        }

        const sumOfCarriedValues = NumberUtils.sumIgnoringInfiniteNumbers(carriedValues, undefined);

        if (
            (this.currentAvailableRetainedEarnings == null ||
                !NumberUtils.isFinite(this.currentAvailableRetainedEarnings)) &&
            (sumOfCarriedValues == null || NumberUtils.isFinite(sumOfCarriedValues))
        ) {
            this.currentCarriedForwardValue = null;
        } else {
            this.currentCarriedForwardValue = NumberUtils.subtractNumbers(
                this.currentAvailableRetainedEarnings ?? 0,
                sumOfCarriedValues ?? 0,
            );
        }
    }

    private calculatePreviousCarriedForwardValue(): void {
        const carriedValues: Array<number | null> = [];
        // we need to omit the last 2 entries from calculation
        for (let rowIndex = FIRST_ELEMENT_INDEX; rowIndex < this.tableEntries.length - 2; rowIndex++) {
            carriedValues.push(this.tableEntries[rowIndex].previousValue);
        }

        const sumOfCarriedValues = NumberUtils.sumIgnoringInfiniteNumbers(carriedValues, undefined);

        if (
            (this.previousAvailableRetainedEarnings == null ||
                !NumberUtils.isFinite(this.previousAvailableRetainedEarnings)) &&
            (sumOfCarriedValues == null || NumberUtils.isFinite(sumOfCarriedValues))
        ) {
            this.previousCarriedForwardValue = null;
        } else {
            this.previousCarriedForwardValue = NumberUtils.subtractNumbers(
                this.previousAvailableRetainedEarnings ?? 0,
                sumOfCarriedValues ?? 0,
            );
        }
    }

    addRow(): void {
        const indexForNewEntry = this.tableEntries.length - 2;
        // we need to add a new row directly above the last 2 rows (last 2 objects in profitCalculationTableData)
        this.tableEntries.splice(indexForNewEntry, FIRST_ELEMENT_INDEX, {
            name: null,
            currentValue: null,
            previousValue: null,
        });
        this.updateForm();
    }

    removeAt(index: number): void {
        debug('remove index', index);
        this.tableEntries.splice(index, 1);
        this.calculateCurrentCarriedForwardValue();
        this.calculatePreviousCarriedForwardValue();
        this.updateForm();
        const preparedOutput: IAopProposal = this.prepareProposalOutputData();
        debug('proposalOutput', preparedOutput);
        this.proposalOutput.emit(preparedOutput);
    }

    private updateCalculatedTableData(): void {
        const carriedForwardTableEntry = this.tableEntries.find(
            (tableEntry) => tableEntry.name === CARRIED_FORWARD_ROW_NAME,
        );

        if (carriedForwardTableEntry != null) {
            carriedForwardTableEntry.currentValue = this.currentCarriedForwardValue;
            carriedForwardTableEntry.previousValue = this.previousCarriedForwardValue;
        }

        const availableRetainedEarningsTableEntry = this.tableEntries.find(
            (tableEntry) => tableEntry.name === AVAILABLE_RETAINED_EARNINGS_ROW_NAME,
        );
        if (availableRetainedEarningsTableEntry != null) {
            availableRetainedEarningsTableEntry.currentValue = this.currentAvailableRetainedEarnings;
            availableRetainedEarningsTableEntry.previousValue = this.previousAvailableRetainedEarnings;
        }

        this.updateForm();

        // emit the initial input data incase no change in form happens
        const preparedOutput: IAopProposal = this.prepareProposalOutputData();
        debug('proposalOutput', preparedOutput);

        this.proposalOutput.emit(preparedOutput);
    }

    private prepareProposalOutputData(): IAopProposal {
        const outputData: IAopProposal = {
            proposalEntries: cloneDeep<IAppropriationOfProfitEntry[]>(this.tableEntries),
            proposalDescription: this.proposalDescription,
            proposalCarriedForwardText: this.proposalCarriedForwardText,
            proposalAvailableRetainedEarningText: this.proposalAvailableRetainedEarningText || this.getDynamicAvailableRetainedEarningText(),
        };

        if (outputData.proposalEntries.length) {
            // for removing the last 2 entries
            outputData.proposalEntries.splice(-2);
            // for removing empty rows
            outputData.proposalEntries = outputData.proposalEntries.filter(
                (entry) => entry.name != null || entry.currentValue != null,
            );
        }

        return outputData;
    }

    // ─── AOP Description checkbox / tiptap ───────────────────────────────────

    onAopDescriptionCheckboxChange(checked: boolean): void {
        if (!checked && this.aopDescription) {
            this.aopDescriptionCheckboxControl.setValue(true, { emitEvent: false });
            this.showRemoveAopDescriptionConfirm = true;
        } else if (checked) {
            this.showAopDescription = true;
            setTimeout(() => this.ensureTiptapEditor(), 0);
        } else {
            this.showAopDescription = false;
            this.destroyTiptap();
            this.aopDescriptionChange.emit('');
        }
    }

    confirmRemoveAopDescription(): void {
        this.showRemoveAopDescriptionConfirm = false;
        this.aopDescriptionCheckboxControl.setValue(false, { emitEvent: false });
        this.aopDescription = '';
        this.showAopDescription = false;
        this.destroyTiptap();
        this.aopDescriptionChange.emit('');
    }

    cancelRemoveAopDescription(): void {
        this.showRemoveAopDescriptionConfirm = false;
        this.showAopDescription = true;
    }

    async onTiptapImageSelected(file: File): Promise<void> {
        if (!file?.type?.startsWith('image/')) {
            return;
        }
        try {
            const base64 = await convertFileToBase64(file);
            if (!base64) {
                return;
            }
            this.tiptapEditor?.commands?.focus?.();
            (this.tiptapEditor as any)?.commands?.setImage?.({ src: base64 });
        } catch (error) {
            console.error('Failed to upload image', error);
        }
    }

    private initAopDescription(value: string): void {
        this.aopDescription = value;
        this.showAopDescription = true;
        this.aopDescriptionCheckboxControl.setValue(true, { emitEvent: false });
        this.aopDescriptionChange.emit(value);
        setTimeout(() => this.ensureTiptapEditor(), 0);
    }

    private ensureTiptapEditor(): void {
        if (this.tiptapEditor || !this.aopDescriptionHost?.nativeElement) {
            return;
        }

        const extensions: AnyExtension[] = [
            StarterKit as unknown as AnyExtension,
            Underline as unknown as AnyExtension,
            OrderedTextAlign.configure({ types: ['heading', 'paragraph'] }) as unknown as AnyExtension,
            LineHeight,
            Indent,
            RichImage.configure({ inline: false }) as unknown as AnyExtension,
            PlaceholderMention.configure({
                HTMLAttributes: { class: 'mention' },
                suggestion: this.createMentionSuggestion(),
            }) as unknown as AnyExtension,
            Table.configure({ resizable: true }) as unknown as AnyExtension,
            TableRow as unknown as AnyExtension,
            TableHeader as unknown as AnyExtension,
            TableCell as unknown as AnyExtension,
            PageBreak,
            TabSpan,
            TableStyle,
            TableCellStyle,
        ];

        this.tiptapEditor = new Editor({
            element: this.aopDescriptionHost.nativeElement,
            extensions,
            content: applyColgroupWidths(this.aopDescription ?? ''),
            onUpdate: ({ editor }) => {
                this.aopDescription = editor.getHTML();
                this.aopDescriptionChange.emit(this.aopDescription);
            },
        });
    }

    private destroyTiptap(): void {
        this.tiptapEditor?.destroy();
        this.tiptapEditor = null;
    }

    private createMentionSuggestion(): any {
        let popup: HTMLDivElement | null = null;
        let list: HTMLDivElement | null = null;
        let selectedIndex = 0;
        let currentItems: string[] = [];
        let currentCommand: ((payload: { id: string; label: string }) => void) | null = null;

        const updateList = (items: string[]) => {
            if (!list) {
                return;
            }
            list.innerHTML = '';
            items.forEach((item: string, index: number) => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = 'tiptap-mention-item';
                if (index === selectedIndex) {
                    button.classList.add('is-selected');
                }
                button.textContent = item;
                button.addEventListener('click', () => {
                    currentCommand?.({ id: item, label: item });
                });
                list?.appendChild(button);
            });
        };

        const updatePosition = (props: any) => {
            const rect = props.clientRect?.();
            if (!rect || !popup) {
                return;
            }
            popup.style.left = `${rect.left + window.scrollX}px`;
            popup.style.top = `${rect.bottom + window.scrollY + 6}px`;
        };

        return {
            char: '@',
            items: ({ query }: { query: string }) =>
                SUPPORTED_PLACE_HOLDERS.filter((item) => item.toLowerCase().includes(query.toLowerCase())),
            render: () => ({
                onStart: (props: any) => {
                    selectedIndex = 0;
                    popup = document.createElement('div');
                    popup.className = 'tiptap-mention-list';
                    list = document.createElement('div');
                    popup.appendChild(list);
                    document.body.appendChild(popup);
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : null;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onUpdate: (props: any) => {
                    selectedIndex = 0;
                    currentItems = (props.items as string[] | undefined) ?? [];
                    currentCommand = typeof props.command === 'function' ? props.command : currentCommand;
                    updateList(currentItems);
                    updatePosition(props);
                },
                onKeyDown: (props: any) => {
                    if (!list) {
                        return false;
                    }
                    if (currentItems.length === 0) {
                        return false;
                    }
                    if (props.event.key === 'ArrowDown') {
                        selectedIndex = (selectedIndex + 1) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'ArrowUp') {
                        selectedIndex = (selectedIndex - 1 + currentItems.length) % currentItems.length;
                        updateList(currentItems);
                        return true;
                    }
                    if (props.event.key === 'Enter') {
                        const item = currentItems[selectedIndex];
                        if (item && typeof currentCommand === 'function') {
                            currentCommand({ id: item, label: item });
                            return true;
                        }
                    }
                    if (props.event.key === 'Escape') {
                        return true;
                    }
                    return false;
                },
                onExit: () => {
                    popup?.remove();
                    popup = null;
                    list = null;
                    currentItems = [];
                    currentCommand = null;
                },
            }),
        };
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    public onSaveProposalAvailableRetainedEarnings(proposalAvailableRetainedEarningText: string): void {
        this.proposalAvailableRetainedEarningText = proposalAvailableRetainedEarningText;
        const preparedPropasal = this.prepareProposalOutputData();
        this.proposalOutput.emit(preparedPropasal);
    }

    public onSaveProposalCarriedForward(proposalCarriedForwardText: string): void {
        this.proposalCarriedForwardText = proposalCarriedForwardText;
        const preparedPropasal = this.prepareProposalOutputData();

        this.proposalOutput.emit(preparedPropasal);
    }

    public onProposalDescriptionChange(proposalDescription: string): void {
        this.proposalDescription = proposalDescription;
        const preparedPropasal = this.prepareProposalOutputData();
        this.proposalOutput.emit(preparedPropasal);
    }

    private getDynamicProposalDescription(): string {
        const currentYear = this.currentAvailableRetainedEarnings;
        const previousYear = this.previousAvailableRetainedEarnings;
        // If both years are positive
        if (currentYear > 0 && previousYear > 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_proposal_description_profit, {
                lng: this.reportLocale,
            }) as string;
        }
        // If both years are negative
        if (currentYear < 0 && previousYear < 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_proposal_description_loss, {
                lng: this.reportLocale,
            }) as string;
        }
        // Mixed results, zeros, or any other scenario
        return this.localizedTexts.t(this.local.aumico_annual_report_proposal_description, {
            lng: this.reportLocale,
        }) as string;
    }
    private getDynamicAvailableRetainedEarningText(): string {
        const currentYear = this.currentAvailableRetainedEarnings;
        const previousYear = this.previousAvailableRetainedEarnings;
        // If both years are positive
        if (currentYear > 0 && previousYear > 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_proposal_available_retained_earnings_profit, {
                lng: this.reportLocale,
            }) as string;
        }
        // If both years are negative
        if (currentYear < 0 && previousYear < 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_proposal_available_retained_earnings_loss, {
                lng: this.reportLocale,
            }) as string;
        }
        // Mixed results, zeros, or any other scenario
        return this.localizedTexts.t(this.local.aumico_annual_report_proposal_available_retained_earnings, {
            lng: this.reportLocale,
        }) as string;
    }
    private getDynamicCarriedForwardLabel(): string {
        const currentYear = this.currentAvailableRetainedEarnings;
        const previousYear = this.previousAvailableRetainedEarnings;
        // If both years are positive
        if (currentYear > 0 && previousYear > 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_proposal_carried_forward_to_new_account_profit, {
                lng: this.reportLocale,
            }) as string;
        }
        // If both years are negative
        if (currentYear < 0 && previousYear < 0) {
            return this.localizedTexts.t(this.local.aumico_annual_report_proposal_carried_forward_to_new_account_loss, {
                lng: this.reportLocale,
            }) as string;
        }
        // Mixed results, zeros, or any other scenario
        return this.localizedTexts.t(this.local.aumico_annual_report_proposal_carried_forward_to_new_account, {
            lng: this.reportLocale,
        }) as string;
    }
}
